import React, { useEffect, useState } from "react";
import Modal from "./Modal";
import { useData } from "../context/DataContext";
import {
  formatKES,
  currentMonth,
  utilityChargeForMonth,
  utilityBalanceForMonth,
} from "../utils/format";
import { MessageSquare, CheckCircle2, XCircle, Loader2, Clock } from "lucide-react";

function buildUtilityMessage(tenant, payments, utilityCharges, property) {
  const month = currentMonth();
  const parts = [];

  if (property?.waterEnabled) {
    const wc = utilityChargeForMonth(utilityCharges, tenant.id, month, "water");
    const wb = utilityBalanceForMonth(utilityCharges, payments, tenant.id, month, "water");
    if (wc > 0) {
      if (wb <= 0) parts.push({ type: "water", status: "paid", amount: wc, balance: 0 });
      else parts.push({ type: "water", status: "unpaid", amount: wc, balance: wb });
    }
  }
  if (property?.electricityEnabled) {
    const ec = utilityChargeForMonth(utilityCharges, tenant.id, month, "electricity");
    const eb = utilityBalanceForMonth(utilityCharges, payments, tenant.id, month, "electricity");
    if (ec > 0) {
      if (eb <= 0) parts.push({ type: "electricity", status: "paid", amount: ec, balance: 0 });
      else parts.push({ type: "electricity", status: "unpaid", amount: ec, balance: eb });
    }
  }

  const unpaid = parts.filter((p) => p.status === "unpaid");
  const allPaid = unpaid.length === 0 && parts.length > 0;

  if (allPaid) {
    const names = parts.map((p) => p.type).join(" and ");
    return `Hi ${tenant.name}, thank you for paying your ${names} bill for house ${tenant.houseNumber}. We appreciate your timely payment!`;
  }

  if (unpaid.length === 0) {
    return `Hi ${tenant.name}, no utility bills have been entered for house ${tenant.houseNumber} this month.`;
  }

  const details = unpaid
    .map((p) => `${p.type} bill of ${formatKES(p.balance)}`)
    .join(" and ");
  const total = unpaid.reduce((a, p) => a + p.balance, 0);

  return `Hi ${tenant.name}, your ${details} for house ${tenant.houseNumber} ${unpaid.length === 1 ? "is" : "are"} outstanding. Total utility balance: ${formatKES(total)}. Please arrange payment. Thank you.`;
}

export default function UtilitySmsDialog({ open, onClose, tenant }) {
  const { addCommunication, payments, utilityCharges, properties } = useData();
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const property = properties.find((p) => p.id === tenant?.propertyId);

  useEffect(() => {
    if (!open || !tenant) return;
    setMessage(buildUtilityMessage(tenant, payments, utilityCharges, property));
    setResult(null);
  }, [open, tenant, payments, utilityCharges, property]);

  if (!tenant) return null;

  const handleClose = () => {
    setResult(null);
    onClose();
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!message.trim() || sending) return;
    setSending(true);
    setResult(null);
    try {
      const c = await addCommunication({ tenantId: tenant.id, type: "sms", message: message.trim() });
      setResult(c?.status === "failed" ? "failed" : "sent");
    } catch (err) {
      console.error("Utility SMS send failed:", err);
      setResult("failed");
    }
    setSending(false);
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Send utility reminder"
      description={`${tenant.name} · House ${tenant.houseNumber}`}
    >
      {result ? (
        <div className="space-y-4">
          {result === "sent" ? (
            <div className="flex items-start gap-3 rounded-lg border border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950 p-4">
              <CheckCircle2 className="h-5 w-5 flex-none text-green-600 dark:text-green-400 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-green-800 dark:text-green-300">SMS sent successfully</p>
                <p className="mt-1 text-sm text-green-700 dark:text-green-400">
                  Utility reminder delivered to {tenant.phone || tenant.name}.
                </p>
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950 p-4">
              <XCircle className="h-5 w-5 flex-none text-red-600 dark:text-red-400 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-red-800 dark:text-red-300">SMS failed to send</p>
                <p className="mt-1 text-sm text-red-700 dark:text-red-400">
                  Could not deliver to {tenant.phone || tenant.name}. Please check the phone number and try again.
                </p>
              </div>
            </div>
          )}
          <div className="flex justify-end">
            <button type="button" className="btn-primary" onClick={handleClose}>
              Done
            </button>
          </div>
        </div>
      ) : (
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="label-base">Message</label>
          <textarea
            className="input-base min-h-[120px] resize-y"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            required
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {message.length} characters · {Math.ceil(message.length / 160)} SMS
          </p>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-secondary" onClick={handleClose}>
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={sending}>
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessageSquare className="h-4 w-4" />}
            {sending ? "Sending…" : "Send SMS"}
          </button>
        </div>
      </form>
      )}
    </Modal>
  );
}
